<template>
  <section class="alumni-section">
    <div class="alumni-wrapper">
      <Motion
        :initial="{ opacity: 0, y: 30 }"
        :whileInView="{ opacity: 1, y: 0 }"
        :inViewOptions="{ once: true, margin: '-80px' }"
        :transition="{ duration: 0.7, ease: 'easeOut' }"
      >
        <div class="section-header">
          <h2 class="section-title">Companies Hiring From <span class="gradient-text">Our Ecosystem</span></h2>
          <p class="section-subtitle">Leading companies where our graduates have built successful careers</p>
        </div>
      </Motion>

      <Motion
        :initial="{ opacity: 0, y: 40 }"
        :whileInView="{ opacity: 1, y: 0 }"
        :inViewOptions="{ once: true, margin: '-80px' }"
        :transition="{ duration: 0.8, ease: 'easeOut' }"
      >
      <div class="carousel-container">
        <!-- First Row - scrolls left to right -->
        <div class="carousel-row">
          <div class="carousel-track carousel-track-left">
            <div v-for="(logo, index) in firstRow" :key="`row1-${index}`" class="logo-item" :style="{ '--index': index }">
              <img :src="logo.src" :alt="getAltText(logo.src)" class="company-logo" />
            </div>
            <div v-for="(logo, index) in firstRow" :key="`row1-dup-${index}`" class="logo-item" :style="{ '--index': index + firstRow.length }">
              <img :src="logo.src" :alt="getAltText(logo.src)" class="company-logo" />
            </div>
          </div>
        </div>

        <!-- Second Row - scrolls right to left -->
        <div class="carousel-row">
          <div class="carousel-track carousel-track-right">
            <div v-for="(logo, index) in secondRow" :key="`row2-${index}`" class="logo-item" :style="{ '--index': index }">
              <img :src="logo.src" :alt="getAltText(logo.src)" class="company-logo" />
            </div>
            <div v-for="(logo, index) in secondRow" :key="`row2-dup-${index}`" class="logo-item" :style="{ '--index': index + secondRow.length }">
              <img :src="logo.src" :alt="getAltText(logo.src)" class="company-logo" />
            </div>
          </div>
        </div>

        <!-- Third Row - scrolls left to right -->
        <div class="carousel-row">
          <div class="carousel-track carousel-track-left-slow">
            <div v-for="(logo, index) in thirdRow" :key="`row3-${index}`" class="logo-item" :style="{ '--index': index }">
              <img :src="logo.src" :alt="getAltText(logo.src)" class="company-logo" />
            </div>
            <div v-for="(logo, index) in thirdRow" :key="`row3-dup-${index}`" class="logo-item" :style="{ '--index': index + thirdRow.length }">
              <img :src="logo.src" :alt="getAltText(logo.src)" class="company-logo" />
            </div>
          </div>
        </div>
      </div>
      </Motion>
    </div>
  </section>
</template>

<script setup lang="ts">
const logos = [
  { src: "/acceture_ori.svg" },
  { src: "/cognizant_ori.svg" },
  { src: "/epam_ori.svg" },
  { src: "/genpact_ori.svg" },
  { src: "/kotak_ori.svg" },
  { src: "/nference_ori.svg" },
  { src: "/saint_ori.svg" },
  { src: "/xpressbees_ori.svg" },
  { src: "/acuvate_ori.svg" },
  { src: "/hcl_ori.svg" },
  { src: "/lg_ori.svg" },
  { src: "/nttdata_ori.svg" },
  { src: "/sigmoid_ori.svg" },
  { src: "/yokogawa_ori.svg" },
  { src: "/hdfc_ori.svg" },
  { src: "/adobe_ori.svg" },
  { src: "/credera_ori.svg" },
  { src: "/ericsson_ori.svg" },
  { src: "/zs_ori.svg" },
  { src: "/akamai_ori.svg" },
  { src: "/css_ori.svg" },
  { src: "/eurofins_ori.svg" },
  { src: "/hgs_ori.svg" },
  { src: "/mercedes_ori.svg" },
  { src: "/ola_ori.svg" },
  { src: "/tataelxsi_ori.svg" },
  { src: "/amdocs_ori.svg" },
  { src: "/deloitte_ori.svg" },
  { src: "/ey_ori.svg" },
  { src: "/hp_ori.svg" },
  { src: "/oracle_ori.svg" },
  { src: "/tcs_ori.svg" },
  { src: "/micro_ori.svg" },
  { src: "/americanexpress_ori.svg" },
  { src: "/dhl_ori.svg" },
  { src: "/ey_ori_new.svg" },
  { src: "/ibm_ori.svg" },
  { src: "/musigma_ori.svg" },
  { src: "/paypal_ori.svg" },
  { src: "/techmahindra_ori.svg" },
  { src: "/bosch_ori.svg" },
  { src: "/ebay_ori.svg" },
  { src: "/fedex_ori.svg" },
  { src: "/icici_ori.svg" },
  { src: "/muthootfinance_ori.svg" },
  { src: "/paytm_ori.svg" },
  { src: "/techmojjo_ori.svg" },
]

// Split logos into three rows
const thirdPoint = Math.ceil(logos.length / 3)
const firstRow = logos.slice(0, thirdPoint)
const secondRow = logos.slice(thirdPoint, thirdPoint * 2)
const thirdRow = logos.slice(thirdPoint * 2)

function getAltText(src: string): string {
  return src
    .replace('/_ori.svg', '')
    .replace(/_/g, ' ')
    .replace(/\//g, '')
    .replace(/\.svg$/, '')
    .replace(/\b\w/g, (l) => l.toUpperCase())
}
</script>

<style scoped>
.alumni-section {
  padding: 2rem 0 8rem 0;
  position: relative;
  z-index: 10;
  overflow: hidden;
}

.alumni-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(ellipse at 50% 50%, rgba(255, 120, 0, 0.08) 0%, transparent 70%);
  pointer-events: none;
}

.alumni-wrapper {
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
}

.section-header {
  text-align: center;
  margin-bottom: 2rem;
}

.section-title {
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 1rem;
}

.gradient-text {
  background: linear-gradient(135deg, #ff7800, #ff7800);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-subtitle {
  font-size: 1.125rem;
  color: var(--text-secondary);
  max-width: 500px;
  margin: 0 auto;
}

.carousel-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow: hidden;
  mask-image: linear-gradient(to right,
      transparent,
      black 10%,
      black 90%,
      transparent);
  -webkit-mask-image: linear-gradient(to right,
      transparent,
      black 10%,
      black 90%,
      transparent);
  padding: 2rem;
}

.carousel-row {
  overflow: hidden;
  width: 100%;
}

.carousel-track {
  display: flex;
  gap: 3rem;
  width: max-content;
  will-change: transform;
}

.carousel-track-left {
  animation: scroll-left 35s linear infinite;
}

.carousel-track-right {
  animation: scroll-right 40s linear infinite;
}

.carousel-track-left-slow {
  animation: scroll-left 45s linear infinite;
}

.logo-item {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 160px;
  height: 80px;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  animation: glow-pulse 3s ease-in-out infinite;
  animation-delay: calc(var(--index, 0) * 0.2s);
}

[data-theme="light"] .logo-item {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.logo-item:hover {
  transform: scale(1.08);
  box-shadow: 0 8px 24px rgba(255, 120, 0, 0.3);
  background: #ffffff;
  animation-play-state: paused;
}

.company-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: all 0.3s ease;
}

.logo-item:hover .company-logo {
  transform: scale(1.05);
}

@keyframes scroll-left {
  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(-50%);
  }
}

@keyframes scroll-right {
  0% {
    transform: translateX(-50%);
  }

  100% {
    transform: translateX(0);
  }
}

@keyframes glow-pulse {
  0%, 100% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
  50% {
    box-shadow: 0 4px 20px rgba(255, 120, 0, 0.25);
  }
}

.carousel-row:hover .carousel-track {
  animation-play-state: paused;
}

@media (max-width: 768px) {
  .alumni-section {
    padding: 2rem 0 3rem 0;
  }

  .alumni-wrapper {
    padding: 0 1rem;
  }

  .logo-item {
    width: 120px;
    height: 65px;
    padding: 0.5rem 0.75rem;
  }

  .carousel-track {
    gap: 1.5rem;
  }

  .carousel-track-left {
    animation: scroll-left 25s linear infinite;
  }

  .carousel-track-right {
    animation: scroll-right 30s linear infinite;
  }

  .carousel-track-left-slow {
    animation: scroll-left 35s linear infinite;
  }
}
</style>
